import { useFlags, useFlagsStatus } from '@unleash/proxy-client-react';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getEnabledExperiments, getVariantPayload } from '../featureFlags';
import { getJSONOffers } from '../services/parse';
import {
  COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG,
  COOKIE_KEY_PLAN_TAG,
  COOKIE_KEY_SELECTED_OFFER_ID,
} from '../constants/cookies';
import { cookies, setCrossDomainCookie } from '../utils/cookieUtils';
import { useSendEventWhenFFReady } from '../hooks/useAnalyticsHook';
import { getAnalyticsPlanType } from '../services/analytics';
import { findPlanById, getHighestPricedPlan, } from '../containers/Home/sections/PaymentModule/planUtils';

const determineInitialSelectedOffer = (planOptions, initialOfferId) => {
  if (!planOptions || !planOptions.length) {
    return null;
  }

  if (initialOfferId) {
    const plan = planOptions.find(plan => plan.planId === initialOfferId);
    if (plan) {
      return plan;
    }
  }

  const highestPricedPlan = getHighestPricedPlan(planOptions);
  return highestPricedPlan;
};

function useSelectedOffer(planOptions, initialOfferId) {
  // TODO For next experiment, move this logic if the experiment is turned on
  const initialSelectedOffer = determineInitialSelectedOffer(planOptions, initialOfferId);
  // END TODO
  const [selectedOfferId, setSelectedOfferId] = useState(
    initialSelectedOffer && initialSelectedOffer.planId
  );
  const sendEventWhenFFReady = useSendEventWhenFFReady();

  const selectedOffer = findPlanById(planOptions, selectedOfferId);

  const sendPlanSelectedEvent = useCallback(
    (planId, extraProps = {}) => {
      const offer = findPlanById(planOptions, planId);
      sendEventWhenFFReady(
        'web_plan_selected',
        {
          app: 'web-site',
          plan_type_selected: offer && getAnalyticsPlanType(offer),
          page_URL: window.location.pathname,
          ...extraProps,
        },
        true
      );
    },
    [planOptions, sendEventWhenFFReady]
  );

  const setNewOffer = useCallback(planId => {
    setCrossDomainCookie(COOKIE_KEY_SELECTED_OFFER_ID, planId);
    setSelectedOfferId(planId);
  }, []);

  const setOfferAndSendAnalytics = useCallback(
    (planId, analyticsProps) => {
      setNewOffer(planId);
      sendPlanSelectedEvent(planId, analyticsProps);
    },
    [sendPlanSelectedEvent, setNewOffer]
  );

  useEffect(() => {
    // TODO For next experiment, move this logic if the experiment is turned on
    // When experiment changes planTag, we should update the selected offer
    if (planOptions.length) {
      const initialSelectedOffer = determineInitialSelectedOffer(planOptions, initialOfferId);
      setNewOffer(initialSelectedOffer.planId);
    }
    // END TODO
  }, [planOptions, setNewOffer]);

  return {
    selectedOffer,
    setSelectedOffer: setOfferAndSendAnalytics,
    selectedOfferId,
    sendPlanSelectedEvent,
  };
}

// Setup plans depending on enabled a/b variant and planTag in variable payload.
// Due to fooaccounts being on separate subdomain, we are capturing and extra
// constant - COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG, that will be used to determine if
// the saved planTag in the cookies is set by an experiment. When an experiment is
// turned off, this cookie will be deleted and the default plans should show.
// Should a user override the activePlanTag via query param, COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG
// will not be set so it does not get wiped out. An experiment will always have overwrite the
// planTag though, if it exists
function useABPlans(setPlanTag, setAndPersistPlanTag) {
  const flags = useFlags();
  const { flagsReady } = useFlagsStatus();

  useEffect(() => {
    if (flagsReady) {
      const enabledExperiments = getEnabledExperiments(flags);

      let hasVariantPlanOverride = false;
      enabledExperiments.forEach(enabledExperiment => {
        const variantPayload = getVariantPayload(enabledExperiment.variant);

        if (variantPayload && variantPayload.planTag) {
          hasVariantPlanOverride = true;
          setAndPersistPlanTag(variantPayload.planTag);
          setCrossDomainCookie(COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG, 'true');
        }
      });

      const isCookieExperimentPlanTag = !!cookies.get(COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG);
      // Reset plans to default if they're not in a pricing experiment or if there is no override in cookies
      if (!hasVariantPlanOverride && isCookieExperimentPlanTag) {
        setPlanTag(DEFAULT_TAG);
        cookies.remove(COOKIE_KEY_IS_EXPERIMENT_PLAN_TAG);
      }
    }
  }, [flags, setPlanTag, setAndPersistPlanTag, flagsReady]);
}

const SubscriptionPlansContext = createContext();

const DEFAULT_TAG = 'default';
const defaultInitialPlans = [];
function SubscriptionPlanProvider({
  children,
  // initialPlans and initialOfferId is determined from ssr based on query params and cookies
  // However, it could be change on the browser due to experiments (only runs on browser) changing them.
  initialPlans = defaultInitialPlans,
  initialOfferId,
  initialPlanTag = DEFAULT_TAG,
}) {
  const [planTag, setPlanTag] = useState(initialPlanTag);
  const [{ isLoading, plans }, setPlans] = useState({
    isLoading: false,
    plans: initialPlans,
  });
  const selectedOfferProps = useSelectedOffer(plans, initialOfferId);

  const updateAndResetPlanTag = useCallback(tag => {
    cookies.remove(COOKIE_KEY_PLAN_TAG);
    setPlanTag(tag);
  }, []);
  const updateAndPersistPlanTag = useCallback(tag => {
    setCrossDomainCookie(COOKIE_KEY_PLAN_TAG, tag);
    setPlanTag(tag);
  }, []);

  useEffect(() => {
    setPlans({
      isLoading: true,
      plans: [],
    });
  }, [planTag]);

  useABPlans(updateAndResetPlanTag, updateAndPersistPlanTag);

  useEffect(() => {
    // If planTag is updated while the previous plans are still loading (async),
    // we want to prevent the previous data from being saved to state.
    // There is a race condition if the second request returns data back
    // before the first request and the plans are updated with wrong planTag
    // data
    let isCanceled = false;

    const loadSubscriptionPlans = async () => {
      const params = { tag: planTag === DEFAULT_TAG ? null : planTag };
      const newPlans = await getJSONOffers(params);

      if (!isCanceled) {
        setPlans({
          isLoading: false,
          plans: newPlans,
        });
      }
    };

    loadSubscriptionPlans();

    // Set the previous request to true to prevent it's data from being saved to state
    // using closure
    return () => {
      isCanceled = true;
    };
  }, [planTag]);

  const value = {
    planTag,
    setPlanTag: updateAndResetPlanTag,
    setAndPersistPlanTag: updateAndPersistPlanTag,
    plans,
    isLoading,
    ...selectedOfferProps,
  };
  return (
    <SubscriptionPlansContext.Provider value={value}>{children}</SubscriptionPlansContext.Provider>
  );
}

const useSubscriptionPlanContext = () => useContext(SubscriptionPlansContext);

export {
  useSubscriptionPlanContext,
  SubscriptionPlanProvider,
  // support react classes
  SubscriptionPlansContext,
};



// WEBPACK FOOTER //
// ./src/subscriptionPlans/context.js